import React from 'react'
import { RefObject, useCallback, useEffect, useRef, useState } from 'react'
import ExpandButton from '@dustin-web/microsite-components/src/expand-button'
import Checkbox from '@dustin-web/microsite-components/src/checkbox'
import { useQueryState, useTranslations } from '@dustin-web/microsite-components/src/hooks'
import classNames from 'classnames'
import { FacetGroup } from '.'
import { DigitalData } from '@dustin-web/microsite-components/src/utils/digital-data'

type Props = {
  facetGroup: FacetGroup
  className?: string
}

export const FacetGroupMultiple = ({ facetGroup, className }: Props) => {
  const { translate } = useTranslations()
  const [fullyExpanded, setFullyExpanded] = useState(false)
  const { getActiveFilterValue, setQueryState } = useQueryState()
  const activeFilters = getActiveFilterValue(facetGroup.nameInUrl)
  const [filtersState, setFiltersState] = useState<{ filters: string[]; shouldQuery: boolean }>({
    filters: activeFilters,
    shouldQuery: false,
  })
  const [initialLoad, setInitialLoad] = useState<boolean>(true)
  const indexRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const selectedFilters = getActiveFilterValue(facetGroup.nameInUrl)
    setFiltersState({ filters: selectedFilters, shouldQuery: false })
  }, [getActiveFilterValue, facetGroup.nameInUrl])

  const queryWithNewFilters = useCallback(() => {
    const selectedFilters = getActiveFilterValue(facetGroup.nameInUrl)
    const { filters, shouldQuery } = filtersState ?? {}

    if (selectedFilters.toString() === filters.toString() || !shouldQuery) {
      return
    }

    const encodedFilters = filters.map(value => encodeURIComponent(value))

    setQueryState(
      {
        [facetGroup.nameInUrl]: encodedFilters.join('|'),
        page: '',
      },
      false
    )
  }, [filtersState, facetGroup.nameInUrl, getActiveFilterValue, setQueryState])

  useEffect(() => queryWithNewFilters(), [filtersState, queryWithNewFilters])

  const facetWithHits = facetGroup.values.filter(value => {
    return value.hits > 0
  })

  const tempNoValidateAttribute = {
    noValidate: true,
  }

  const onCheckboxChange = ({ name, hits }: (typeof facetWithHits)[0], facetSelected: boolean) => {
    if (!hits && !facetSelected) {
      return
    }

    const { filters } = filtersState ?? {}
    const isFilterSelected = filters.some(
      filter => filter === name || filter.toLowerCase() === name.toLowerCase()
    )

    if (filters.length > 0 && isFilterSelected) {
      setFiltersState({
        filters: filters.filter(
          filter => filter !== name && filter.toLowerCase() !== name.toLowerCase()
        ),
        shouldQuery: true,
      })
    } else {
      setFiltersState({ filters: [...filters, name], shouldQuery: true })
      DigitalData.pushFacetClick(facetGroup.displayEnglishName)
    }
  }

  const getRef = (index: number) => {
    return index === 8 ? (indexRef as RefObject<HTMLDivElement>) : null
  }

  useEffect(() => {
    if (initialLoad) {
      setInitialLoad(false)
      return
    }
    if (indexRef.current) {
      indexRef.current.tabIndex = -1

      indexRef.current.focus()
    }
  }, [fullyExpanded])

  return (
    <form className={classNames('c-form u-pb-16 u-px-16', className)} {...tempNoValidateAttribute}>
      <fieldset className="u-mb-0">
        {facetWithHits.map((facet, index) => {
          const facetSelected = filtersState.filters.some(
            filter => filter.toLocaleLowerCase() == facet.name.toLocaleLowerCase()
          )
          const facetTitle = facetGroup.isTranslated
            ? `${facet.displayName ?? ''} ${facetGroup.unitText}`
            : `${translate(`FacetValue_${facet.name}`)} ${facetGroup.unitText}`

          return (
            (index < 8 || fullyExpanded || facetWithHits.length <= 9 || facetSelected) && (
              <Checkbox
                indexRef={getRef(index)}
                key={index}
                className={classNames('c-filter-facet-group__facet', {
                  'c-filter-facet-group__facet--active': facetSelected,
                })}
                labelClassName="c-filter-facet-group__facet-label"
                label={
                  <>
                    <div
                      className="c-filter-facet-group__facet-title u-overflow-ellipsis"
                      title={facetTitle}>
                      {facetTitle}
                    </div>
                    <div id="facet-hits" className="u-color-mute">
                      {facet.hits}
                    </div>
                  </>
                }
                data-test-id={`facet-checkbox-${facet.name}`}
                name={facet.name}
                disabled={!facet.hits && !facetSelected}
                checked={facetSelected}
                onChange={() => onCheckboxChange(facet, facetSelected)}
              />
            )
          )
        })}
      </fieldset>
      {facetWithHits.length > 9 && (
        <div className="u-pt-16 d-flex">
          <ExpandButton
            collapsedTitle={translate('ExpandFacets')}
            expandedTitle={translate('CollapseFacets')}
            expanded={fullyExpanded}
            onClick={e => {
              e.preventDefault()
              setFullyExpanded(prevState => !prevState)
            }}
            ariaControls={''}
          />
        </div>
      )}
    </form>
  )
}
